import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBookmarkNote } from '../../hooks/use-bookmarks';
import { notifyError } from '../../lib/toast';
import { Icon } from '../shared/Icons';

interface BookmarkNoteEditorProps {
  bookmarkId: string;
  note: string | null;
}

// A personal note attached to a saved bookmark — the design's "Personal notes attached" box. Reads
// as an italic quote in a left-accented card; click to edit inline. Saves on blur / Enter (Shift+Enter
// for a newline), reverts on Escape or a failed save. Nothing is sent when the text is unchanged.
export function BookmarkNoteEditor({ bookmarkId, note }: BookmarkNoteEditorProps) {
  const { t } = useTranslation();
  const update = useUpdateBookmarkNote();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note ?? '');
  const ref = useRef<HTMLTextAreaElement>(null);

  // Keep the local value in sync when the note changes underneath us (a refetch) while not editing.
  useEffect(() => {
    if (!editing) setValue(note ?? '');
  }, [note, editing]);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  async function save() {
    const next = value.trim();
    setEditing(false);
    if (next === (note ?? '')) return; // unchanged → no request
    try {
      await update.mutateAsync({ id: bookmarkId, note: next });
    } catch (err) {
      notifyError(err, t('bookmarks.noteSaveErrorToast'));
      setValue(note ?? ''); // revert on failure
    }
  }

  if (editing) {
    return (
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void save();
          }
          if (e.key === 'Escape') {
            setValue(note ?? '');
            setEditing(false);
          }
        }}
        rows={2}
        maxLength={500}
        placeholder={t('bookmarks.addNotePlaceholder')}
        className="w-full resize-none rounded-sm border border-accent bg-surface px-3 py-2 text-[13px] text-ink outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group/note flex w-full items-start gap-2 rounded-sm border-l-2 border-accent bg-bg-2 px-3 py-2 text-left transition-colors hover:bg-surface-2"
    >
      <span className={`min-w-0 flex-1 text-[13px] italic ${note ? 'text-ink-2' : 'text-muted'}`}>
        {note ? `“${note}”` : t('bookmarks.addNotePlaceholder')}
      </span>
      <Icon.pencil className="mt-0.5 h-3 w-3 flex-none text-muted opacity-0 transition-opacity group-hover/note:opacity-100" />
    </button>
  );
}
